import React, { useEffect, useState, useRef } from 'react';
import { Icon } from '@iconify/react';
import $ from 'jquery';
import { getPaginatedWorkOrders, getWorkOrderById } from '../../api/work-orderAPI';
import { getWorkOrderPartsByWorkOrderId } from '../../api/work-order-partsAPI';
import { getBillOfEquipmentWosByWorkOrderId } from '../../api/bill-of-equipment-woAPI';
import { getBillOfLabourWosByWorkOrderId } from '../../api/bill-of-labour-woAPI';
import { getBillOfServicesWosByWorkOrderId } from '../../api/bill-of-services-woAPI';
import { getWorkOrderFormsByWorkOrderId } from '../../api/work-order-formsAPI';
import { getAll } from '../../api';
import { fetchData } from '../../utils/apiHelpers';
import WorkOrderViewModal from '../WorkOrderViewModal';
import { formatIntDate } from "../../utils/dateUtils.js";

const AssetWorkOrders = ({ assetId, loading = false }) => {
  const [workOrders, setWorkOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showView, setShowView] = useState(false);
  const [currentWorkOrder, setCurrentWorkOrder] = useState(null);
  const [parts, setParts] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [labour, setLabour] = useState([]);
  const [services, setServices] = useState([]);
  const [forms, setForms] = useState([]);
  const [crew, setCrew] = useState([]);

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchWorkOrders = async () => {
    try {
      const resp = await getPaginatedWorkOrders({ page: 1, pageSize: 100, assetId });
      setWorkOrders(resp.data || []);
    } catch (err) {
      console.error('Error loading asset work orders:', err);
    }
  };

  const fetchWorkOrderDetails = async (wo) => {
    try {
      const partsData = await getWorkOrderPartsByWorkOrderId(wo.id);
      const equip = await getBillOfEquipmentWosByWorkOrderId(wo.id);
      const lab = await getBillOfLabourWosByWorkOrderId(wo.id);
      const serv = await getBillOfServicesWosByWorkOrderId(wo.id);
      const formsData = await getWorkOrderFormsByWorkOrderId(wo.id);
      setParts(partsData);
      setEquipment(equip);
      setLabour(lab);
      setServices(serv);
      setForms(formsData);
    } catch (err) {
      console.error('Error loading work order details:', err);
      setParts([]);
      setEquipment([]);
      setLabour([]);
      setServices([]);
      setForms([]);
    }
  };

  const handleView = async (wo) => {
    try {
      const fresh = await getWorkOrderById(wo.id);
      setCurrentWorkOrder(fresh);
      await fetchWorkOrderDetails(fresh);
      setShowView(true);
    } catch (err) {
      console.error('Error loading work order:', err);
    }
  };

  useEffect(() => {
    fetchWorkOrders();
    fetchData(getAll, { apiName: 'users' }, setCrew);
  }, [assetId]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (workOrders.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rt',
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [workOrders]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  return (
    <>
      <div className="col-xxl-12 mt-16">
        <div className="card h-100 radius-8 border-0">
          <div className="card-body p-24">
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <h6 className="mb-2 fw-bold text-lg mb-0">Work Orders</h6>
              <input
                type="text"
                className="form-control w-auto"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div
              className="table-responsive"
              style={{ maxHeight: 'calc(100vh - 688px)' }}
            >
              <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
                <thead>
                  <tr>
                    <th>Number</th>
                    <th>Title</th>
                    <th>Status</th>
                    <th>Due Date</th>
                    <th>Completed At</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {workOrders.length === 0 && !loading && (
                    <tr>
                      <td colSpan="5" className="text-center py-4 rounded-0">
                        No Work Orders Found
                      </td>
                    </tr>
                  )}
                  {workOrders.map((wo) => {
                    const statusColour = wo.status?.colours?.bootstrapName || 'secondary';
                    return (
                      <tr key={wo.id}>
                        <td>{wo.workOrderNumber}</td>
                        <td>{wo.workOrderTitle}</td>
                        <td>
                          {wo.status && (
                            <span
                              className={`bg-${statusColour}-focus text-${statusColour}-main px-16 py-2 rounded-pill fw-medium text-sm`}
                            >
                              {wo.status.name}
                            </span>
                          )}
                        </td>
                        <td>{wo.dueDate ? formatIntDate(wo.dueDate) : ''}</td>
                        <td>{wo.completedAt ? formatIntDate(wo.completedAt) : ''}</td>
                        <td>
                          <button
                            className="view-btn w-32-px h-32-px bg-info-focus text-info-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            onClick={() => handleView(wo)}
                          >
                            <Icon icon="ic:baseline-visibility" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {loading && (
                    <tr>
                      <td colSpan="5" className="text-center py-2">
                        <span className="spinner-border spinner-border-sm" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {showView && (
        <WorkOrderViewModal
          workOrder={currentWorkOrder}
          parts={parts}
          equipment={equipment}
          labour={labour}
          services={services}
          forms={forms}
          onViewForm={() => { }}
          crew={crew}
          onClose={() => setShowView(false)}
        />
      )}
    </>
  );
};

export default AssetWorkOrders;